import { jsx as _jsx } from "react/jsx-runtime";
import { camelToDisplay } from '@iotv/datamodel';
import { getZombieInstance, isNonNaNNumber, SortOrder } from '@iotv/iotv-v3-types';
import { Alert, Skeleton, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useReducer } from 'react';
import { ObjectViewType } from '../../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { StripedDataGrid } from '../../factories/DataGrid';
import { ObservationsContext } from '../Contexts/OvesrvationsContextType_V2';
import { UseObservationsContext } from '../Contexts/UseObservationsContext';
import { ObservationInputMetaKeys } from '../fns';
import { DateColRendererEditWithApi, DateTimeColRendererWithApi, DefaultCellEditRendererWithApi, DefaultCellRendererWithApi, durationValueGetter, interRowDurationValueGetter, } from '../gridColFns';
import { metricItemsToVOBs } from '../helperFns';
import { ActionIcons } from '../../../Iconography';
import { KinesisVideoStreamContext } from '../../VideoStream/Context/KinesisVideoStreamContext';
import { useFragmentList } from '../../VideoStream/common/useFragmentList';
import { HistoryStoreContext } from '../../../ContextProviders/HistoryStoreContext';
import { BinaryChoiceRadio } from '../..//common/BinaryChoice';
import { TimestampFragementImageCell } from '../../VideoStream/common/TimestampFragmentImageCell';
const debug = process.env.REACT_APP_DEBUG && false;
const defaultLinkedTableState = {
    selectedStoreName: null,
    selectedStore: null,
    dataTimeRange: {
        fromTimestamp: null,
        toTimestamp: null,
    },
};
const storeChoice = {
    Readings: 'Readings',
    Observations: 'Observations',
};
const LinkedTableReducer = (prev, next) => ({ ...prev, ...next });
export const useLinkedTable = ({ fillHeight = false }) => {
    const [localState, localDispatch] = useReducer(LinkedTableReducer, defaultLinkedTableState);
    const { selectedStore, selectedStoreName, dataTimeRange } = localState;
    const { historyStore: readingsStore } = useContext(HistoryStoreContext);
    const { state: ObservationContextState, dispatch: observationsStateDispatch } = useContext(ObservationsContext);
    const { state: KinesisVideoStreamState, dispatch: kinesisDispatch } = useContext(KinesisVideoStreamContext);
    const { persistedObservationHistory, save, deleteObject, working, querying, errMessage } = useContext(UseObservationsContext);
    const { observationHistory } = ObservationContextState;
    const recentStateHistory = readingsStore?.recentStateHistory;
    const observationHistoryStore = observationHistory?.observedHistory ?? null;
    const { Fragments } = useFragmentList({ fromTimestamp: dataTimeRange.fromTimestamp, toTimestamp: dataTimeRange.toTimestamp });
    const { kvaClient, StreamInfoList, selectedStreamInfo, VideoStream } = KinesisVideoStreamState;
    debug && console.log('historyStore', observationHistoryStore);
    const observationKeys = (observationHistoryStore?.keys() ?? []).filter((k) => !ObservationInputMetaKeys.includes(k));
    const observatinStateSnapshots = selectedStore ? selectedStore.getStateSnapshotArray(SortOrder.DESCENDING) : [];
    const pseudoTypeId = `HistoryStore_observation`;
    const observationMetricItems = selectedStore
        ? metricItemsToVOBs({
            psuedoParentVob: persistedObservationHistory ?? getZombieInstance({ type: 'Never', id: 'Never' }),
            pseudoTypeId,
            stateSnapshots: observatinStateSnapshots,
        })
        : [];
    const getRowTimeRange = (row) => {
        const { fromTimestampMs, toTimestampMs, timestamp } = row;
        if (isNonNaNNumber(fromTimestampMs) && isNonNaNNumber(toTimestampMs)) {
            return {
                fromTimestamp: Math.min(fromTimestampMs, toTimestampMs),
                toTimestamp: Math.max(fromTimestampMs, toTimestampMs),
            };
        }
        if (isNonNaNNumber(timestamp)) {
            return {
                fromTimestamp: timestamp,
                toTimestamp: timestamp,
            };
        }
        return null;
    };
    useEffect(() => {
        if (selectedStore) {
            let minTimestamp = selectedStore?.getFirstTimestamp() ?? null;
            let maxTimestamp = selectedStore?.getLastTimestamp() ?? null;
            if (minTimestamp === null || maxTimestamp === null) {
                minTimestamp = dataTimeRange.fromTimestamp;
                maxTimestamp = dataTimeRange.toTimestamp;
            }
            const nextRange = { fromTimestamp: minTimestamp, toTimestamp: maxTimestamp };
            localDispatch({ dataTimeRange: nextRange });
        }
    }, [selectedStore,]);
    useEffect(() => {
        if (selectedStoreName === null && selectedStore === null) {
            if (observationHistoryStore) {
                localDispatch({ selectedStoreName: 'Observations', selectedStore: observationHistoryStore });
            }
            else if (readingsStore) {
                localDispatch({ selectedStoreName: 'Readings', selectedStore: readingsStore });
            }
        }
    }, [readingsStore, observationHistoryStore]);
    useEffect(() => {
        if (selectedStoreName === 'Observations') {
            localDispatch({ selectedStore: observationHistoryStore });
        }
        else if (selectedStoreName === 'Readings') {
            localDispatch({ selectedStore: readingsStore });
        }
    }, [selectedStoreName]);
    const handleSetVideoTimeFrame = (gp) => {
        const { row, api, rowNode } = gp;
        const { fromTimestampMs, toTimestampMs, timestamp } = row;
        let fromTimestamp = null;
        let toTimestamp = null;
        if (isNonNaNNumber(fromTimestampMs) && isNonNaNNumber(toTimestampMs)) {
            fromTimestamp = fromTimestampMs;
            toTimestamp = toTimestampMs;
        }
        else if (isNonNaNNumber(timestamp)) {
            const idx = api.getAllRowIds().findIndex((id) => id === rowNode.id);
            const nextRowId = api.getRowIdFromRowIndex(idx + 1);
            const nextRow = nextRowId ? api.getRow(nextRowId) : null;
            if (nextRow) {
                const nextRowTimestamp = nextRow?.timestamp ?? null;
                if (isNonNaNNumber(nextRowTimestamp)) {
                    fromTimestamp = Math.min(timestamp, nextRowTimestamp);
                    toTimestamp = Math.max(timestamp, nextRowTimestamp);
                    console.log(`Setting video time frame from row ${idx} timestamp ${new Date(timestamp).toString()} to next row timestamp ${new Date(nextRowTimestamp).toString()}`);
                }
                else {
                    console.log(`No valid next row timestamp found for row ${idx}`, nextRowTimestamp);
                }
            }
            else {
                console.log(`No next row found for row ${idx}`, row);
            }
        }
        else {
            console.log(`No valid timestamp found in row`, row);
        }
        if (!isNonNaNNumber(fromTimestamp) || !isNonNaNNumber(toTimestamp)) {
            console.log(`Skipping video time frame update: invalid timestamp range`, { fromTimestamp, toTimestamp, rowId: rowNode.id });
            return;
        }
        if (KinesisVideoStreamState.fromTimestamp === fromTimestamp && KinesisVideoStreamState.toTimestamp === toTimestamp) {
            console.log(`Skipping video time frame update: unchanged timestamp range`, { fromTimestamp, toTimestamp, rowId: rowNode.id });
            return;
        }
        kinesisDispatch({ fromTimestamp, toTimestamp });
    };
    const [columnVisibilityModel] = useColumnVisibilityForDataGrid({
        dataTableReference: 'Observations',
        objectTypeId: 'HistoryStore',
        viewType: ObjectViewType.List,
    });
    const observationsBaseCols = [
        {
            headerName: '',
            field: 'id',
            renderCell: (gp) => (_jsx(ActionIcons.DoMagic, { onClick: () => {
                    console.log('TODOD CLICK');
                    handleSetVideoTimeFrame(gp);
                },
                sx: { cursor: 'pointer' } })),
            width: 16,
        },
        // { field: 'localTimeStr', headerName: 'Time', width: 250 },
        {
            field: 'fromTimestampMs',
            headerName: 'From',
            flex: 1,
            renderCell: DateTimeColRendererWithApi,
            editable: true,
            renderEditCell: DateColRendererEditWithApi,
        },
        {
            field: 'toTimestampMs',
            headerName: 'To',
            flex: 1,
            renderCell: DateTimeColRendererWithApi,
            editable: true,
            renderEditCell: DateColRendererEditWithApi,
        },
        { field: '_duraction', headerName: 'Duration', width: 150, valueGetter: durationValueGetter },
    ];
    const readingsBaseCols = [
        // {
        //   headerName: '',
        //   field: 'id',
        //   renderCell: (gp) => (
        //     <ActionIcons.DoMagic
        //       {...{
        //         onClick: () => {
        //           console.log('TODOD CLICK')
        //           handleSetVideoTimeFrame(gp)
        //         },
        //         sx: { cursor: 'pointer' },
        //       }}
        //     />
        //   ),
        //   width: 16,
        // },
        // { field: 'localTimeStr', headerName: 'Time', width: 250 },
        {
            field: 'timestamp',
            headerName: 'Time',
            flex: 1,
            renderCell: DateTimeColRendererWithApi,
            editable: true,
            renderEditCell: DateColRendererEditWithApi,
        },
        { field: '_duraction', headerName: 'Duration', width: 150, valueGetter: interRowDurationValueGetter },
    ];
    const baseCols = selectedStoreName === storeChoice.Readings ? readingsBaseCols : observationsBaseCols;
    // Default passthrough renderer for DataGrid cells
    const dynamicCols = observationKeys.map((k) => {
        const colDef = {
            field: k,
            headerName: camelToDisplay(k),
            editable: true,
            flex: 1,
            renderCell: DefaultCellRendererWithApi,
            renderEditCell: DefaultCellEditRendererWithApi,
        };
        return colDef;
    });
    const descriptionsCol = {
        field: 'description',
        headerName: 'Description',
        editable: true,
        flex: 1,
        renderCell: DefaultCellRendererWithApi,
        renderEditCell: DefaultCellEditRendererWithApi,
    };
    const imageCol = {
        field: '_image',
        headerName: 'Image?',
        width: 16,
        renderCell: (params) => {
            if (kvaClient && selectedStreamInfo?.StreamARN && Fragments) {
                return _jsx(TimestampFragementImageCell, { params: params, client: kvaClient, StreamARN: selectedStreamInfo.StreamARN, Fragments: Fragments, handleSetVideoTimeFrame: handleSetVideoTimeFrame }, `${VideoStream?.sk ?? 'none'}:${selectedStreamInfo.StreamARN}:${String(params.id)}:${dataTimeRange.fromTimestamp ?? 'none'}:${dataTimeRange.toTimestamp ?? 'none'}`);
            }
            else {
                return _jsx(Typography, { variant: "body2", color: "textSecondary", children: "No stream" });
            }
        }
    };
    const columns = [imageCol, ...baseCols, ...dynamicCols, descriptionsCol];
    const processRowUpdate = (newRow, oldRow) => {
        debug && console.log(`processRowUpdate got `, { newRow, oldRow });
        const idx = observationMetricItems.findIndex((item) => item.id === newRow.id);
        debug && console.log('processRowUpdate edit ', { idx, observationMetricItems });
        if (observationHistoryStore) {
            const idx = observationHistoryStore.timestamp.findIndex((t) => newRow.timestamp === t);
            const action = { type: 'editObservation', value: { idx, newRow, oldRow } };
            console.log(`TEMP processRowUpdate dispatching edit action`, { action });
            observationsStateDispatch(action);
        }
        else {
            console.log(`TEMP processRowUpdate no observationHistoryStore, cannot dispatch edit action`);
        }
        return newRow;
    };
    const tableRowHeight = 24;
    const maxVisibleRows = 25;
    const dataGridProps = {
        columns,
        columnHeaderHeight: tableRowHeight,
        rowHeight: tableRowHeight,
        rows: [...observationMetricItems].reverse(), // reverse to show most recent at top
        loading: querying,
        columnVisibilityModel,
        pagination: true,
        pageSizeOptions: [maxVisibleRows],
        initialState: {
            pagination: {
                paginationModel: { pageSize: maxVisibleRows, page: 0 },
            },
        },
        processRowUpdate: selectedStoreName === storeChoice.Observations ? processRowUpdate : undefined,
        onProcessRowUpdateError: (e) => {
            console.log(e.message);
        },
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        // fix table height to show 10 rows, with scroll for more
    };
    const DatasourceSelector = selectedStoreName ? (_jsx(BinaryChoiceRadio, { currentValue: selectedStoreName,
        onChange: (e) => localDispatch({ selectedStoreName: e.target.value }),
        valueA: 'Readings',
        valueB: 'Observations',
        labelA: 'Readings',
        labelB: 'Observations' })) : null;
    const headerHeight = tableRowHeight;
    const linkedTableHeightPx = tableRowHeight * maxVisibleRows + headerHeight;
    const linkedTableHeight = linkedTableHeightPx;
    const LinkedTable = (_jsx(Stack, { gap: 1, width: "100%", height: linkedTableHeight, maxHeight: linkedTableHeight, children: !selectedStore ? (_jsx(Alert, { severity: "info", sx: { width: '100%' }, children: `Select either Readings or Observations` })) : querying ? (_jsx(Skeleton, { variant: "rounded", height: "8em" }, "ObsGridSkeleton")) : (_jsx("div", { style: { height: linkedTableHeight, maxHeight: linkedTableHeight, width: '100%' }, children: _jsx(StripedDataGrid, { ...dataGridProps }) })) }, 'LinkedTableStack'));
    return { LinkedTable, DatasourceSelector, selectedStoreName, dataTimeRange };
};
